const VERSION_URL = '/version.json';

/** Throttle hard reloads so a broken deploy cannot infinite-loop the tab. */
const RELOAD_COOLDOWN_MS = 10_000;
const RELOAD_TS_KEY = 'genie-deploy-reload-ts';

let initialBuildId: string | null = null;
let initialFetchDone = false;

function tryHardReload(): void {
  const now = Date.now();
  const last = sessionStorage.getItem(RELOAD_TS_KEY);
  if (last !== null && now - Number(last) < RELOAD_COOLDOWN_MS) {
    return;
  }
  sessionStorage.setItem(RELOAD_TS_KEY, String(now));
  window.location.reload();
}

async function fetchBuildId(): Promise<string | null> {
  try {
    const res = await fetch(VERSION_URL, { cache: 'no-store' });
    if (!res.ok) {
      return null;
    }
    const data = (await res.json()) as { buildId?: string };
    return typeof data.buildId === 'string' ? data.buildId : null;
  } catch {
    return null;
  }
}

/**
 * Ensures long-lived tabs pick up new deploys (version.json) and recovers from
 * stale chunk references after a deploy (dynamic import / script failures).
 */
export function registerDeployRefreshListeners(): void {
  void fetchBuildId().then((id) => {
    initialBuildId = id;
    initialFetchDone = true;
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible' || !initialFetchDone) {
      return;
    }
    void fetchBuildId().then((id) => {
      if (
        id !== null &&
        initialBuildId !== null &&
        id !== initialBuildId
      ) {
        tryHardReload();
      }
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const msg =
      typeof reason === 'object' &&
      reason !== null &&
      'message' in reason &&
      typeof (reason as { message: unknown }).message === 'string'
        ? (reason as Error).message
        : String(reason ?? '');
    if (
      /Loading chunk \d+ failed|ChunkLoadError|Failed to fetch dynamically imported module|Importing a module script failed/i.test(
        msg
      )
    ) {
      event.preventDefault();
      tryHardReload();
    }
  });

  window.addEventListener(
    'error',
    (event) => {
      const t = event.target;
      if (
        t instanceof HTMLScriptElement &&
        typeof t.src === 'string' &&
        t.src.includes('/assets/')
      ) {
        tryHardReload();
      }
    },
    true
  );
}
